import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { NumField, CurrencySelect, Money, currencySymbol, num } from "./shared";
import { shippingModel, DEFAULT_DIVISOR, type Units } from "./shippingModel";

const TOOL_INFO_CONTENT = {
  what: `Estimates shipping costs including dimensional weight — the box-size-based charge that catches many sellers off guard, since carriers charge based on whichever is greater: actual weight or dimensional weight. Works in pounds/inches or kilograms/centimetres.`,
  how: `Enter your carrier rate, package weight and dimensions. The billable weight is rounded up to the next whole pound or kilo (as carriers do), and the total shipping cost and a suggested customer charge are calculated.`,
  tip: `A large, lightweight box can cost more to ship than a small, heavy one because of dimensional weight — using appropriately-sized packaging is a real, ongoing cost lever for anyone shipping regularly. Check your carrier's divisor: 139 (in³/lb) is common in the US, 5000 (cm³/kg) elsewhere.`,
};

export default function ShippingCostEstimator() {
  const [currency, setCurrency] = useState("USD");
  const [units, setUnits] = useState<Units>("imperial");
  const [baseRate, setBaseRate] = useState("5.00");
  const [perUnit, setPerUnit] = useState("0.75");
  const [weight, setWeight] = useState("2");
  const [length, setLength] = useState("12");
  const [width, setWidth] = useState("9");
  const [height, setHeight] = useState("4");
  const [divisor, setDivisor] = useState("139");
  const [packaging, setPackaging] = useState("0.50");
  const [markup, setMarkup] = useState("0");
  const [roundUp, setRoundUp] = useState(true);
  const sym = currencySymbol(currency);
  const wU = units === "imperial" ? "lb" : "kg", dU = units === "imperial" ? "in" : "cm";

  const changeUnits = (u: Units) => { setUnits(u); setDivisor(String(DEFAULT_DIVISOR[u])); };

  const v = { baseRate: num(baseRate), perUnit: num(perUnit), weight: num(weight), l: num(length), w: num(width), h: num(height), divisor: num(divisor, DEFAULT_DIVISOR[units]), packaging: num(packaging), markupPct: num(markup) };
  const valid = Object.values(v).every(Number.isFinite) && v.divisor > 0 && v.weight >= 0 && v.l >= 0 && v.w >= 0 && v.h >= 0;
  const errs = {
    weight: v.weight < 0 ? "Weight cannot be negative." : undefined,
    l: v.l < 0 ? "Length cannot be negative." : undefined,
    w: v.w < 0 ? "Width cannot be negative." : undefined,
    h: v.h < 0 ? "Height cannot be negative." : undefined,
    divisor: v.divisor <= 0 ? "Divisor must be greater than zero (139 for US carriers, 5000 for metric)." : undefined,
  };
  const hasNeg = Object.values(errs).some(Boolean);
  const result = useMemo(() => (valid ? shippingModel({ units, roundUp, ...v }) : null), [valid, units, roundUp, baseRate, perUnit, weight, length, width, height, divisor, packaging, markup]); // eslint-disable-line react-hooks/exhaustive-deps
  const { blocked } = useLogToolUsage(true, 3000);

  return (
    <ToolContainer>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <CurrencySelect value={currency} onChange={setCurrency} />
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Units</Label>
          <Select value={units} onValueChange={(u) => changeUnits(u as Units)}>
            <SelectTrigger className="h-8 w-[190px]" aria-label="Units"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="imperial">Pounds / inches</SelectItem><SelectItem value="metric">Kilograms / centimetres</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <p className="text-sm font-medium">Carrier rate (enter your actual negotiated/published rate)</p>
          <div className="grid grid-cols-2 gap-4">
            <NumField label={`Base rate (${sym})`} value={baseRate} onChange={setBaseRate} />
            <NumField label={`Per ${wU} (${sym})`} value={perUnit} onChange={setPerUnit} />
          </div>
          <NumField label={`Actual weight (${wU})`} value={weight} onChange={setWeight} error={errs.weight} />
          <p className="text-sm font-medium pt-2">Dimensional weight (box size)</p>
          <div className="grid grid-cols-3 gap-3">
            <NumField label={`L (${dU})`} value={length} onChange={setLength} error={errs.l} />
            <NumField label={`W (${dU})`} value={width} onChange={setWidth} error={errs.w} />
            <NumField label={`H (${dU})`} value={height} onChange={setHeight} error={errs.h} />
          </div>
          <NumField label={`Dim divisor (${dU}³ per ${wU})`} value={divisor} onChange={setDivisor} error={errs.divisor} hint="139 is common for domestic US, 5000 for metric; carriers vary, so check yours" />
          <div className="flex items-center gap-2"><Checkbox id="ru" checked={roundUp} onCheckedChange={(c) => setRoundUp(!!c)} /><Label htmlFor="ru" className="font-normal cursor-pointer">Round billable weight up to the next whole {wU} (how carriers bill)</Label></div>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <NumField label={`Packaging cost (${sym})`} value={packaging} onChange={setPackaging} />
            <NumField label="Markup to charge customer (%)" value={markup} onChange={setMarkup} />
          </div>
        </div>
        <div className="space-y-3">
          {blocked ? <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p> : !result ? (
            <p className="text-sm text-red-600 py-4">{hasNeg ? "Fix the highlighted fields (no negative values, divisor above zero) to see the estimate." : "Enter valid numbers to see the estimate."}</p>
          ) : (<>
            <Card className="p-4"><p className="text-xs text-muted-foreground">Dimensional weight</p><p className="text-xl font-bold">{result.dimWeight.toFixed(2)} {wU}</p></Card>
            <Card className="p-4"><p className="text-xs text-muted-foreground">Billable weight (greater of actual vs. dim{roundUp ? ", rounded up" : ""})</p><p className="text-xl font-bold" data-testid="se-billable">{roundUp ? result.billable : result.billable.toFixed(2)} {wU}</p></Card>
            <Card className="p-4"><p className="text-xs text-muted-foreground">Your total shipping cost</p><p className="text-xl font-bold text-primary"><Money n={result.totalCost} currency={currency} /></p></Card>
            <Card className="p-4 border-primary/40"><p className="text-xs text-muted-foreground">Suggested charge to customer</p><p className="text-2xl font-bold text-primary"><Money n={result.charge} currency={currency} /></p></Card>
          </>)}
        </div>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
